import React, { useState, useCallback } from 'react';

interface SearchFilter {
  type: 'player' | 'item' | 'territory';
  query: string;
  minLevel?: number;
  maxLevel?: number;
  minPieces?: number;
  maxPieces?: number;
  itemType?: string;
  rarity?: string;
  region?: string;
  ownerType?: string;
}

interface AdvancedSearchProps {
  apiBaseUrl: string;
  adminKey: string;
  onSearch: (results: any[], filterType: string) => void;
}

export const AdvancedSearch: React.FC<AdvancedSearchProps> = ({ apiBaseUrl, adminKey, onSearch }) => {
  const [searchType, setSearchType] = useState<'player' | 'item' | 'territory'>('player');
  const [query, setQuery] = useState<string>('');
  const [minLevel, setMinLevel] = useState<string>('');
  const [maxLevel, setMaxLevel] = useState<string>('');
  const [minPieces, setMinPieces] = useState<string>('');
  const [maxPieces, setMaxPieces] = useState<string>('');
  const [itemType, setItemType] = useState<string>('');
  const [rarity, setRarity] = useState<string>('');
  const [region, setRegion] = useState<string>('');
  const [ownerType, setOwnerType] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [savedFilters, setSavedFilters] = useState<Record<string, SearchFilter>>({});

  const performSearch = useCallback(async (): Promise<void> => {
    if (!query.trim()) {
      alert('Enter a search query');
      return;
    }

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('q', query);
      if (minLevel) params.append('minLevel', minLevel);
      if (maxLevel) params.append('maxLevel', maxLevel);
      if (minPieces) params.append('minPieces', minPieces);
      if (maxPieces) params.append('maxPieces', maxPieces);
      if (itemType) params.append('itemType', itemType);
      if (rarity) params.append('rarity', rarity);
      if (region) params.append('region', region);
      if (ownerType) params.append('ownerType', ownerType);

      const endpoint = searchType === 'player'
        ? `/api/admin/prod/search/characters?${params}`
        : searchType === 'item'
        ? `/api/admin/prod/search/items?${params}`
        : `/api/admin/prod/search/territories?${params}`;

      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        headers: { 'x-admin-key': adminKey },
      });

      if (response.ok) {
        const data = await response.json();
        const results = data.results || data.characters || data.items || data.territories || [];
        onSearch(results, searchType);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [query, minLevel, maxLevel, minPieces, maxPieces, itemType, rarity, region, ownerType, searchType, apiBaseUrl, adminKey, onSearch]);

  const saveFilter = (): void => {
    const filterName = prompt('Save filter as:');
    if (filterName) {
      setSavedFilters(prev => ({
        ...prev,
        [filterName]: {
          type: searchType,
          query,
          minLevel: minLevel ? parseInt(minLevel) : undefined,
          maxLevel: maxLevel ? parseInt(maxLevel) : undefined,
          minPieces: minPieces ? parseInt(minPieces) : undefined,
          maxPieces: maxPieces ? parseInt(maxPieces) : undefined,
          itemType,
          rarity,
          region,
          ownerType,
        },
      }));
    }
  };

  const loadFilter = (filterName: string): void => {
    const filter = savedFilters[filterName];
    if (filter) {
      setSearchType(filter.type);
      setQuery(filter.query);
      setMinLevel(filter.minLevel?.toString() || '');
      setMaxLevel(filter.maxLevel?.toString() || '');
      setMinPieces(filter.minPieces?.toString() || '');
      setMaxPieces(filter.maxPieces?.toString() || '');
      setItemType(filter.itemType || '');
      setRarity(filter.rarity || '');
      setRegion(filter.region || '');
      setOwnerType(filter.ownerType || '');
    }
  };

  return (
    <div style={{
      backgroundColor: '#1a1a2e',
      border: '1px solid #4a5568',
      borderRadius: 0,
      padding: 16,
      marginBottom: 16,
      fontFamily: 'monospace',
    }}>
      <h3 style={{ color: '#ffa500', fontSize: 14, margin: '0 0 16px 0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>🔍 ADVANCED SEARCH</h3>

      {/* Search Type Tabs */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 16,
        borderBottom: '1px solid #4a5568',
        paddingBottom: 12,
      }}>
        {(['player', 'item', 'territory'] as const).map(type => (
          <button
            key={type}
            onClick={() => setSearchType(type)}
            style={{
              padding: '6px 12px',
              backgroundColor: searchType === type ? '#ffa500' : '#2d3748',
              color: searchType === type ? '#000' : '#e2e8f0',
              border: 'none',
              borderRadius: 0,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Search Query */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
          Search Query
        </label>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && performSearch()}
          placeholder={`Search ${searchType}s...`}
          style={{
            width: '100%',
            padding: '8px 12px',
            backgroundColor: '#2d3748',
            border: '1px solid #4a5568',
            color: '#e2e8f0',
            fontSize: 11,
            borderRadius: 2,
          }}
        />
      </div>

      {/* Filters Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
        marginBottom: 16,
      }}>
        {/* Player Filters */}
        {searchType === 'player' && (
          <>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Min Level
              </label>
              <input
                type="number"
                value={minLevel}
                onChange={(e) => setMinLevel(e.target.value)}
                min="1"
                max="100"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  backgroundColor: '#2d3748',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  fontSize: 11,
                  borderRadius: 2,
                }}
              />
            </div>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Max Level
              </label>
              <input
                type="number"
                value={maxLevel}
                onChange={(e) => setMaxLevel(e.target.value)}
                min="1"
                max="100"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  backgroundColor: '#2d3748',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  fontSize: 11,
                  borderRadius: 2,
                }}
              />
            </div>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Min Pieces
              </label>
              <input
                type="number"
                value={minPieces}
                onChange={(e) => setMinPieces(e.target.value)}
                min="0"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  backgroundColor: '#2d3748',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  fontSize: 11,
                  borderRadius: 2,
                }}
              />
            </div>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Max Pieces
              </label>
              <input
                type="number"
                value={maxPieces}
                onChange={(e) => setMaxPieces(e.target.value)}
                min="0"
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  backgroundColor: '#2d3748',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  fontSize: 11,
                  borderRadius: 2,
                }}
              />
            </div>
          </>
        )}

        {/* Item Filters */}
        {searchType === 'item' && (
          <>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Item Type
              </label>
              <select
                value={itemType}
                onChange={(e) => setItemType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  backgroundColor: '#2d3748',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  fontSize: 11,
                  borderRadius: 2,
                }}
              >
                <option value="">All Types</option>
                <option value="consumable">Consumable</option>
                <option value="material">Material</option>
                <option value="equipment">Equipment</option>
                <option value="weapon">Weapon</option>
                <option value="armor">Armor</option>
              </select>
            </div>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Rarity
              </label>
              <select
                value={rarity}
                onChange={(e) => setRarity(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  backgroundColor: '#2d3748',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  fontSize: 11,
                  borderRadius: 2,
                }}
              >
                <option value="">All Rarities</option>
                <option value="common">Common</option>
                <option value="uncommon">Uncommon</option>
                <option value="rare">Rare</option>
                <option value="epic">Epic</option>
                <option value="legendary">Legendary</option>
              </select>
            </div>
          </>
        )}

        {/* Territory Filters */}
        {searchType === 'territory' && (
          <>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Region
              </label>
              <select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  backgroundColor: '#2d3748',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  fontSize: 11,
                  borderRadius: 2,
                }}
              >
                <option value="">All Regions</option>
                <option value="reaches">The Reaches</option>
                <option value="paradise">Paradise</option>
                <option value="arcadia">Arcadia</option>
              </select>
            </div>
            <div>
              <label style={{ color: '#a0aec0', fontSize: 10, display: 'block', marginBottom: 4 }}>
                Owner Type
              </label>
              <select
                value={ownerType}
                onChange={(e) => setOwnerType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  backgroundColor: '#2d3748',
                  border: '1px solid #4a5568',
                  color: '#e2e8f0',
                  fontSize: 11,
                  borderRadius: 2,
                }}
              >
                <option value="">All Owners</option>
                <option value="player">Player</option>
                <option value="regime">Regime</option>
                <option value="unclaimed">Unclaimed</option>
              </select>
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 12,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: 'monospace',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>
        <button
          onClick={() => performSearch()}
          disabled={isLoading}
          style={{
            width: '100%',
            padding: '8px 12px',
            backgroundColor: isLoading ? '#2d3748' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: 0,
            fontSize: 11,
            fontWeight: 600,
            fontFamily: 'monospace',
            cursor: isLoading ? 'default' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {isLoading ? 'SEARCHING...' : '🔍 SEARCH'}
        </button>
        <button
          onClick={saveFilter}
          style={{
            padding: '8px 12px',
            backgroundColor: '#8b5cf6',
            color: '#fff',
            border: 'none',
            borderRadius: 0,
            fontSize: 11,
            fontWeight: 600,
            fontFamily: 'monospace',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          💾 SAVE FILTER
        </button>
      </div>

      {/* Saved Filters */}
      {Object.keys(savedFilters).length > 0 && (
        <div style={{
          padding: 8,
          backgroundColor: '#0b1220',
          borderRadius: 2,
          fontSize: 10,
        }}>
          <div style={{ color: '#a0aec0', marginBottom: 6 }}>Saved Filters:</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Object.keys(savedFilters).map(filterName => (
              <button
                key={filterName}
                onClick={() => loadFilter(filterName)}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#2d3748',
                  color: '#e2e8f0',
                  border: '1px solid #4a5568',
                  borderRadius: 2,
                  fontSize: 10,
                  cursor: 'pointer',
                }}
              >
                {filterName}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
